import axios from "axios";
import { Fragment } from "react";
import { RiApps2Fill } from "react-icons/ri";
import { useDispatch } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import ButtonOutline from "src/components/atoms/ButtonOutline";
import TextLoader from "src/components/atoms/TextLoader";
import { toggleMenu } from "src/redux/global/menuSlice";
import useSwr from "swr";


const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API ||
  "";

const IMAGE_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_IMAGE_BASE_URL) ||
  process.env.REACT_APP_IMAGE ||
  process.env.REACT_APP_ASSET_BASE_URL ||
  "";

const fetcher = (url) => axios.get(url).then((res) => res.data);

const joinImageURL = (base, path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || /^\/\//.test(path)) return path; // absolute
  if (!base) return path.startsWith("/") ? path : `/${path}`;
  return `${String(base).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
};

const getCategoryImageUrl = (item = {}) => {
  const pick = (v) => (typeof v === "string" ? v : v?.url || v?.src || "");
  const raw =
    pick(item.image) ||
    pick(item.image_url) ||
    pick(item.thumbnail) ||
    pick(item.logo) ||
    pick(item.cover) ||
    pick(item.icon) ||
    pick(item.icon_url);

  return raw ? joinImageURL(IMAGE_BASE, raw) : "/images/placeholders/category.png";
};

const onImgError = (e) => {
  if (e.currentTarget.dataset.fallbackApplied) return;
  e.currentTarget.dataset.fallbackApplied = "1";
  e.currentTarget.src = "/images/placeholders/category.png";
};

const navItemClasses = (isActive) =>
  `flex items-center gap-2 p-3 text-lg ${
    isActive ? "text-white bg-primary" : "text-[#7C7C7C]"
  }`;

const catItemClasses = (isActive) =>
  `group relative flex items-center justify-start pl-3 px-4
   h-10 md:h-14 transition-colors
   ${isActive ? "bg-[#008277] text-white" : "text-[#7C7C7C]"}
   after:content-[''] after:absolute after:inset-0 after:bg-[#2FAB73]/0
   hover:after:bg-[#2FAB73]/10 focus-visible:after:bg-[#2FAB73]/15
   focus-visible:outline-none focus-visible:ring-2
   focus-visible:ring-[#2FAB73]/30 focus-visible:ring-offset-2
   hover:shadow-sm`;

const Sidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, error, isLoading } = useSwr(
    API_BASE ? `${API_BASE}/category` : null,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: true }
  );
  const hasCategories = Array.isArray(data) && data.length > 0;

  const goToPage = (page) => {
    navigate(page);
    dispatch(toggleMenu(false));
  };

  return (
    <aside className="relative z-10 min-h-screen pt-24 md:pt-0 pb-24 md:pb-0">
      <div className="max-h-[calc(100vh-6rem)] overflow-y-auto pr-1">
        <NavLink to="/product" className={({ isActive }) => navItemClasses(isActive)}>
          <div className="w-6">
            <RiApps2Fill fontSize={22} />
          </div>
          <div className="relative font-semibold">All Brands</div>
        </NavLink>

        {isLoading && (
          <div className="px-3 py-2 flex flex-col gap-4">
            <TextLoader /><TextLoader /><TextLoader />
          </div>
        )}
        {error && (
          <div className="px-4 py-3 text-sm text-red-600">
            Gagal memuat kategori. Coba lagi nanti.
          </div>
        )}
        {!isLoading && !error && !hasCategories && (
          <div className="px-4 py-3 text-sm text-[#7C7C7C]">Belum ada kategori.</div>
        )}

        {hasCategories &&
          data.map((item) => (
            <Fragment key={item.id}>
              <NavLink state={item} to={`/ctg/${item.slug}`}>
                {({ isActive }) => (
                  <div
                    className={catItemClasses(isActive)}
                    aria-label={item.name || "Category"}
                    title={item.name || "Category"}
                  >
                    <img
                      src={getCategoryImageUrl(item)}
                      alt={item.name || "Category"}
                      className="block max-h-8 md:max-h-12 w-auto object-contain shrink-0
                                 transition-transform duration-200
                                 group-hover:scale-[1.05] active:scale-[0.97]"
                      loading="lazy"
                      decoding="async"
                      onError={onImgError}
                    />
                  </div>
                )}
              </NavLink>
            </Fragment>
          ))}
      </div>

      <div className="md:hidden block left-0 right-0 absolute bottom-0">
        <div className="flex my-2 justify-center">
          <ButtonOutline onClick={() => goToPage("/contact")} title="Contact Us" />
        </div>
        <nav className="flex-1 p-3 flex justify-between bg-primary text-white">
          <button onClick={() => goToPage("/gallery")}>Gallery</button>
          <button onClick={() => goToPage("/service")}>Service</button>
          <button onClick={() => goToPage("/product")}>Product</button>
          <button onClick={() => goToPage("/articles")}>Articles</button>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
